/**
 * ネットワーク活動が指定時間アイドル状態になるまで待機する
 * Puppeteerの page.waitForLoadState('networkidle') に相当
 *
 * @param timeout アイドル状態と判定するまでの時間（ミリ秒）
 * @returns Promise<void>
 */
export const waitForNetworkIdle = (timeout: number = 500): Promise<void> => {
  return new Promise((resolve) => {
    let activeRequests = 0
    let idleTimer: NodeJS.Timeout | null = null
    let isResolved = false

    const startIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer)
      idleTimer = setTimeout(() => {
        if (!isResolved) {
          isResolved = true
          cleanup()
          resolve()
        }
      }, timeout)
    }

    // オリジナルの関数を保存
    const originalXMLHttpRequestOpen = XMLHttpRequest.prototype.open
    const originalXMLHttpRequestSend = XMLHttpRequest.prototype.send
    const originalFetch = window.fetch

    // XMLHttpRequest を監視
    XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, ...args: any[]) {
      activeRequests++
      if (idleTimer) {
        clearTimeout(idleTimer)
        idleTimer = null
      }
      return originalXMLHttpRequestOpen.apply(this, args as any)
    }

    XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, ...args: any[]) {
      this.addEventListener('loadend', () => {
        activeRequests--
        if (activeRequests === 0 && !isResolved) {
          startIdleTimer()
        }
      })
      this.addEventListener('error', () => {
        activeRequests--
        if (activeRequests === 0 && !isResolved) {
          startIdleTimer()
        }
      })
      this.addEventListener('abort', () => {
        activeRequests--
        if (activeRequests === 0 && !isResolved) {
          startIdleTimer()
        }
      })
      return originalXMLHttpRequestSend.apply(this, args as any)
    }

    // fetch を監視
    window.fetch = function (...args: any[]) {
      activeRequests++
      if (idleTimer) {
        clearTimeout(idleTimer)
        idleTimer = null
      }
      return originalFetch.apply(this, args as any).finally(() => {
        activeRequests--
        if (activeRequests === 0 && !isResolved) {
          startIdleTimer()
        }
      })
    }

    // クリーンアップ関数
    const cleanup = () => {
      XMLHttpRequest.prototype.open = originalXMLHttpRequestOpen
      XMLHttpRequest.prototype.send = originalXMLHttpRequestSend
      window.fetch = originalFetch
      if (idleTimer) {
        clearTimeout(idleTimer)
      }
    }

    // 初期状態でアイドル状態なら即座に解決
    if (activeRequests === 0) {
      startIdleTimer()
    }

    // 最大待機時間でタイムアウト（無限待機を防ぐ）
    setTimeout(() => {
      if (!isResolved) {
        isResolved = true
        cleanup()
        resolve()
      }
    }, 5000) // 5秒でタイムアウト
  })
}

/**
 * ナビゲーション（ページ遷移）の完了を待機する
 * DOM更新とレンダリング完了を検出
 *
 * @param currentPathname 現在のパス名（変更検出用）
 * @returns Promise<void>
 */
export const waitForNavigationComplete = (currentPathname?: string): Promise<void> => {
  return new Promise((resolve) => {
    let isResolved = false

    const resolveOnce = () => {
      if (!isResolved) {
        isResolved = true
        resolve()
      }
    }

    // パスが変更された場合の処理
    if (currentPathname && window.location.pathname !== currentPathname) {
      // パスが変わった場合は追加のフレーム待機
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolveOnce()
        })
      })
      return
    }

    // DOM変更の監視
    let mutationCount = 0
    let lastMutationTime = Date.now()

    const observer = new MutationObserver(() => {
      mutationCount++
      lastMutationTime = Date.now()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'], // パフォーマンス向上のため制限
    })

    // DOM変更が安定するまで待機
    const checkStability = () => {
      const now = Date.now()
      const timeSinceLastMutation = now - lastMutationTime

      // 100ms間DOM変更がなければ安定とみなす
      if (timeSinceLastMutation > 100) {
        observer.disconnect()
        // さらに1フレーム待ってレンダリング完了を確実にする
        requestAnimationFrame(() => {
          resolveOnce()
        })
      } else {
        setTimeout(checkStability, 50)
      }
    }

    // 初期チェック
    setTimeout(checkStability, 100)

    // 最大待機時間でタイムアウト
    setTimeout(() => {
      observer.disconnect()
      resolveOnce()
    }, 2000) // 2秒でタイムアウト
  })
}

/**
 * ページ更新の完了を総合的に待機する
 * ナビゲーション完了 + ネットワークアイドル + DOM安定化を組み合わせ
 *
 * @param currentPathname 現在のパス名
 * @returns Promise<void>
 */
export const waitForPageUpdateComplete = async (currentPathname?: string): Promise<void> => {
  // 1. ナビゲーション完了を待つ
  await waitForNavigationComplete(currentPathname)

  // 2. ネットワークアイドルを待つ
  await waitForNetworkIdle(250)

  // 3. React の状態更新完了を待つ
  await new Promise((resolve) => setTimeout(resolve, 50))
}
